/**
 * Owner request (2026-08-21): every digit gets 50% MORE letter-spacing
 * than its surrounding text, sitewide, without a per-section patch -
 * the other half of the same task that gave digits their own smaller
 * size (assets/custom-typography.css, unicode-range @font-face). That
 * CSS-only mechanism can't reach letter-spacing: a font-face descriptor
 * has no letter-spacing property, and CSS letter-spacing itself only
 * ever applies per-element to a run's FULL text, never to a subset of
 * characters within it - there is no selector or font-matching
 * mechanism that can single out "just the digits" for letter-spacing
 * the way unicode-range can for font-face. Reaching individual digit
 * runs at all requires wrapping them in their own element, which is
 * why this one part of the task needs JS (the CSS mechanism is
 * otherwise reused as-is, unchanged, per the task's own instruction).
 *
 * Behaviour: wraps every run of one or more consecutive digits in any
 * text node in a <span class="js-digit-spacing"> with an explicit
 * letter-spacing of 1.5x whatever the surrounding element's own
 * computed letter-spacing already is (so it tracks the theme's
 * existing responsive/dynamic letter-spacing automatically, never a
 * hardcoded value). Runs once on load, then again for any DOM subtree
 * added afterwards (cart/quantity/price updates, section re-renders,
 * etc.) via a single sitewide MutationObserver - no per-section hooks.
 * Skips script/style/textarea/input/contenteditable and anything
 * already wrapped, so it can never touch editable values (quantity
 * inputs, forms) or double-wrap its own output.
 */
(() => {
  const DIGIT_RUN = /\d+/;
  const SKIP_SELECTOR = 'script, style, textarea, input, [contenteditable], .js-digit-spacing';

  function wrapDigitRuns(node) {
    const parent = node.parentElement;
    if (!parent || parent.closest(SKIP_SELECTOR)) return;
    if (!DIGIT_RUN.test(node.textContent)) return;

    const baseSpacing = parseFloat(getComputedStyle(parent).letterSpacing) || 0;
    const digitSpacing = baseSpacing * 1.5;
    const text = node.textContent;
    const runRegex = /\d+/g;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    while ((match = runRegex.exec(text))) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }
      const span = document.createElement('span');
      span.className = 'js-digit-spacing';
      span.style.letterSpacing = `${digitSpacing}px`;
      span.textContent = match[0];
      fragment.appendChild(span);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    node.parentNode.replaceChild(fragment, node);
  }

  function processSubtree(root) {
    if (root.nodeType === Node.TEXT_NODE) {
      wrapDigitRuns(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    if (root.closest(SKIP_SELECTOR)) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let current;
    while ((current = walker.nextNode())) textNodes.push(current);
    textNodes.forEach(wrapDigitRuns);
  }

  function init() {
    processSubtree(document.body);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((added) => {
          if (added.nodeType === Node.ELEMENT_NODE || added.nodeType === Node.TEXT_NODE) {
            processSubtree(added);
          }
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
